import { Stack, TextField } from '@mui/material';
import { VyIcon, type VyIconType } from '@vy/ui-design-system';
import { useCallback, useState } from 'react';
import { isEmptyString } from '@vy/utils';
import { userMessage } from '../../../../../utils/vy-toasts';
import { PhaseLabelChipIconButton } from './PhaseLabelChipIconButton';

const ENTER_KEY = 'Enter';
const ESCAPE_KEY = 'Escape';

type EditablePhaseLabelChipProps = {
  label: string;
  color: string;
  icon: VyIconType;
  backgroundColor: string;
  onSubmit: (newLabel: string) => void;
  onDismiss: () => void;
  configuration?: {
    warnUnmodified?: boolean;
    allowEmptyValue?: boolean;
  };
};

export const EditablePhaseLabelChip: React.FC<EditablePhaseLabelChipProps> = ({
  label,
  color,
  icon,
  backgroundColor,
  onSubmit,
  onDismiss,
  configuration,
}) => {
  const [editedLabel, setEditedLabel] = useState(label);
  const { warnUnmodified = true, allowEmptyValue = false } = configuration ?? {};

  const handleSubmit = useCallback(() => {
    if (warnUnmodified && editedLabel === label) {
      userMessage('warning', 'The phase label has not been changed');
      onDismiss();
      return;
    }
    if (!allowEmptyValue && isEmptyString(editedLabel)) {
      return userMessage('error', 'Phase label cannot be empty');
    }

    onSubmit(editedLabel);
  }, [editedLabel, label, onSubmit, onDismiss]);

  const handleDismiss = useCallback(() => {
    setEditedLabel(label);
    onDismiss();
  }, [label, onDismiss]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (e.key === ENTER_KEY) {
        return handleSubmit();
      }

      if (e.key === ESCAPE_KEY) {
        return handleDismiss();
      }
    },
    [handleSubmit, handleDismiss],
  );

  const handleStopPropagation = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      e.stopPropagation();
    },
    [],
  );

  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        width: "100%"
      }}>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          backgroundColor,
          color,
          borderRadius: '16px',
          padding: '4px 12px',
          display: 'flex',
          gap: 1,
          minHeight: '32px'
        }}>
        <VyIcon color={color} icon={icon} />
        <TextField
          value={editedLabel}
          onChange={(e) => setEditedLabel(e.target.value)}
          variant="standard"
          size="small"
          autoFocus
          slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                color,
                fontSize: '13px',
                fontWeight: 500,
                '& input': {
                  padding: 0,
                  color,
                },
              },
            },
          }}
          onKeyDown={handleKeyDown}
          onClick={handleStopPropagation}
          onFocus={handleStopPropagation}
        />
      </Stack>
      <PhaseLabelChipIconButton icon="SmallCheckmark" onClick={handleSubmit} />
      <PhaseLabelChipIconButton icon="SmallX" onClick={handleDismiss} />
    </Stack>
  );
};
